import { useEffect, useState } from 'react';
import { useStoreState } from './store';

const CDN_URL = import.meta.env.VITE_APP_LOCALE_CDN as string;

const getCurrentLocale = (state: any) => state?.auth?.user?.locale ?? 'en';

// CDN_URL points at `/_index` (all languages combined, ~237kB). Swap it for a
// single-language file so we only download the active locale.
const localeUrl = (locale: string) =>
  CDN_URL.replace(/\/_index$/, `/${locale}`);

async function cdn(locale: string) {
  try {
    const response = await fetch(localeUrl(locale));
    if (!response.ok) {
      throw new Error(
        `CDN request failed: ${response.status} ${response.statusText}`
      );
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to load translations from CDN:', error);
    // Return empty object, React Intl will use defaultMessage props
    return {};
  }
}

// English is authored in code (defaultMessage props); it is never fetched from
// the CDN. SimpleLocalize's `en` is only the source text for translators.
const EN_LOADED = { locale: 'en', messages: {} as Record<string, any> };

export function useLocaleProvider() {
  const locale = useStoreState(getCurrentLocale);
  // `loaded.locale` records which locale `messages` belongs to, so consumers
  // can tell a resolved (possibly empty) catalog from one still in flight.
  const [loaded, setLoaded] = useState(EN_LOADED);

  useEffect(() => {
    if (locale === 'en') {
      setLoaded(EN_LOADED);
      return;
    }
    let ignore = false;
    cdn(locale)
      .then((updated) => {
        if (!ignore) setLoaded({ locale, messages: updated || {} });
      })
      .catch(() => {
        // Final fallback - empty messages, React Intl will use defaultMessage
        if (!ignore) setLoaded({ locale, messages: {} });
      });
    // Ignore a stale in-flight response if locale changes before it resolves.
    return () => {
      ignore = true;
    };
  }, [locale]);

  return {
    // Serve only a catalog that matches the active locale, so IntlProvider
    // never renders one locale's strings under another's formatting rules.
    messages: loaded.locale === locale ? loaded.messages : {},
    messagesLocale: loaded.locale,
    locale,
  };
}

export const LANGUAGES = {
  cn: {
    name: '中文(简体)',
    dayjsLocaleLoader: () => import('dayjs/locale/zh-cn'),
    dayjsLocaleId: 'zh-cn',
    reactIntlLocale: 'zh-CN',
  },
  de: {
    name: 'German',
    dayjsLocaleLoader: () => import('dayjs/locale/de'),
    reactIntlLocale: 'de-DE',
  },
  en: {
    name: 'English',
    dayjsLocaleLoader: () => import('dayjs/locale/en'),
    reactIntlLocale: 'en-US',
  },
  es: {
    name: 'Spanish',
    dayjsLocaleLoader: () => import('dayjs/locale/es'),
    reactIntlLocale: 'es-ES',
  },
  fr: {
    name: 'French',
    dayjsLocaleLoader: () => import('dayjs/locale/fr'),
    reactIntlLocale: 'fr-FR',
  },
  pt: {
    name: 'Portuguese',
    dayjsLocaleLoader: () => import('dayjs/locale/pt'),
    reactIntlLocale: 'pt-PT',
  },
  tw: {
    name: '中文(繁體)',
    dayjsLocaleLoader: () => import('dayjs/locale/zh-tw'),
    dayjsLocaleId: 'zh-tw',
    reactIntlLocale: 'zh-TW',
  },
};
if (import.meta.env.VITE_APP_ENV !== 'prod') {
  LANGUAGES.ja = {
    name: 'TEST LANGUAGE',
    dayjsLocaleLoader: () => import('dayjs/locale/ja'),
    reactIntlLocale: 'ja-JP',
  };
}

export function useLocale() {
  const current = useStoreState(getCurrentLocale);
  const options = Object.entries(LANGUAGES).sort((a, b) =>
    a[1].name.localeCompare(b[1].name)
  );

  return { current, options };
}
